/**
 * Status pill with a colour and an icon derived from the status itself.
 *
 * The application has one status vocabulary and several places that render it:
 * tenants, memberships, users, invitations, setup readiness. Left to each call
 * site, the same word ends up a different colour in two panels, and colour ends
 * up carrying the meaning on its own — which it cannot, for the roughly one in
 * twelve men with a colour vision deficiency, or for anyone reading a
 * greyscale print of a validation pack. The icon is what makes the state
 * legible; the colour reinforces it.
 *
 * Unknown statuses render neutral with the generic icon rather than throwing,
 * so a new status added to a repository type shows up plainly instead of
 * breaking a page.
 *
 * @module components/ui/StatusBadge
 */

import React from "react";

import { Badge, type BadgeTone } from "./Badge";
import { Icon } from "./Icon";

/** Visual treatments a status can map onto. */
export type StatusTone = "success" | "warning" | "error" | "neutral";

interface StatusAppearance {
  tone: StatusTone;
  icon: string;
}

/**
 * How each known status reads.
 *
 * Keyed by the literal strings the repository types use (`TenantStatus`,
 * `TenantOnboardingStatus`, `UserStatus`, `MembershipStatus`,
 * `TenantSetupStatus`, invitation states), so a status flows from the API to
 * the screen without a per-call-site translation.
 */
const APPEARANCE: Record<string, StatusAppearance> = {
  // Working as intended.
  active: { tone: "success", icon: "check-circle" },
  ready: { tone: "success", icon: "check-circle" },
  accepted: { tone: "success", icon: "check-circle" },
  claimed: { tone: "success", icon: "seal-check" },

  // Waiting on somebody.
  pending: { tone: "warning", icon: "clock" },
  "pending-identity": { tone: "warning", icon: "clock" },
  invited: { tone: "warning", icon: "envelope-simple" },
  "configuration-required": { tone: "warning", icon: "warning-circle" },
  suspended: { tone: "warning", icon: "warning-circle" },
  "in-validation": { tone: "warning", icon: "clock-countdown" },

  // Deliberately stopped.
  revoked: { tone: "error", icon: "x-circle" },
  expired: { tone: "error", icon: "clock-counter-clockwise" },

  // Switched off, not broken.
  deactivated: { tone: "neutral", icon: "minus-circle" },
  inactive: { tone: "neutral", icon: "minus-circle" },
  dormant: { tone: "neutral", icon: "minus-circle" },
};

const FALLBACK: StatusAppearance = { tone: "neutral", icon: "info" };

const TONE_COLOR: Record<StatusTone, BadgeTone> = {
  success: "success",
  warning: "warning",
  error: "error",
  neutral: "neutral",
};

/**
 * Turn a status token into a readable label: `pending-identity` reads
 * "Pending identity". Only the first word is capitalised, so a label sits in a
 * sentence without shouting.
 *
 * @param status - Raw status token.
 * @returns The humanised label.
 */
export function statusLabel(status: string): string {
  // Typed as required, but the values reaching this come from API projections
  // where a field can simply be absent. Breaking the page a status is rendered
  // on is a worse answer than rendering nothing.
  const words = (status ?? "").trim().replace(/[-_]+/g, " ").trim();
  if (!words) {
    return "";
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface StatusBadgeProps {
  /** Status token, e.g. `active`, `pending-identity`, `revoked`. */
  status: string;
  /** Overrides the humanised label when a call site needs different wording. */
  label?: string;
  /** Smaller pill, for dense lists. */
  size?: "sm" | "md";
  /** Additional classes for layout, e.g. `shrink-0`. */
  className?: string;
  "data-testid"?: string;
}

/**
 * Render a status as a coloured, icon-led pill.
 *
 * @example
 * ```tsx
 * <StatusBadge status={tenant.status} size="sm" />
 * <StatusBadge status={user.status} label="Account active" />
 * ```
 */
export function StatusBadge({
  status,
  label = undefined,
  size = "md",
  className = "",
  "data-testid": testId,
}: StatusBadgeProps): React.ReactElement | null {
  const appearance = APPEARANCE[(status ?? "").trim().toLowerCase()] ?? FALLBACK;
  const text = label ?? statusLabel(status);
  // Nothing, rather than a coloured pill whose only content is an
  // `aria-hidden` icon: that is a state chip with no name to read.
  if (!text) {
    return null;
  }
  return (
    <Badge
      tone={TONE_COLOR[appearance.tone]}
      variant="soft"
      size={size}
      className={["gap-1", className].filter(Boolean).join(" ")}
      data-testid={testId}
    >
      {/* Decorative: the label beside it already names the state, so
          announcing the icon too would read the status twice. */}
      <Icon name={appearance.icon} aria-hidden />
      {text}
    </Badge>
  );
}
